import React from "react";
// ConsumerDetail left card

// Default
import { useState } from "react";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";

// Custom
import CustomSearch from "../search/Search";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import MapViewPopover from "../popovers/MapViewPopover";
import BalanceDetailModal from "../modals/BalanceDetailModal";

// Svg
import Location from "../../assets/svg/location";
import Calendar from "../../assets/svg/calendar";
import Clock from "../../assets/svg/clock";

// Theme
import theme from "../../theme";

// Redux
import { setCustomerId } from "../../redux/features/CustomerSlice";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { formatDate } from "../../utils/helper";

const ConsumerDetailLeftCard = ({
  handleSearch,
}: {
  handleSearch: () => void;
}) => {
  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [timeRange, setTimeRange] = useState([0, 1439]);
  const [mapView, setMapView] = useState<HTMLElement | null>(null);
  const [openBalanceModal, setOpenBalanceModal] = useState(false);
  const dispatch = useAppDispatch();
  const { customerDetails, customerId } = useAppSelector(
    (state) => state.customer,
  );

  const handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <Card className="consumer_left_card">
      <Grid container pb={"12.5px"}>
        <Grid size={{ xs: 4, md: 4, lg: 4 }} container alignItems={"center"}>
          <Stack direction={"row"} alignItems={"center"} gap={"10px"}>
            <Typography
              variant="body1"
              fontSize={"13px"}
              sx={{ whiteSpace: "nowrap" }}
            >
              Consumer ID:{" "}
            </Typography>
            <CustomSearch
              value={customerId}
              onChange={(e) => dispatch(setCustomerId(e.target.value))}
              onClick={handleSearch}
              sx={{ width: "400px" }}
            />
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Stack alignItems={"center"}>
            <Typography color="#1064ED" fontWeight={700} fontSize={"15px"}>
              {customerDetails?.name}
            </Typography>
            {customerDetails?.customerCode && (
              <Typography variant="caption" color="textSecondary">
                ({customerDetails.customerCode})
              </Typography>
            )}
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"flex-end"}
          alignItems={"center"}
        >
          <Stack gap={"4px"}>
            <Typography
              variant="body1"
              display={"flex"}
              gap={"4px"}
              alignItems={"center"}
              fontSize={"13px"}
            >
              Registered Contact Number :{" "}
              <Typography
                variant="body1"
                color="#1064ED"
                fontWeight={700}
                letterSpacing={"0.32px"}
                fontSize={"13px"}
              >
                {" "}
                {customerDetails?.registerContactNumber}
              </Typography>
            </Typography>
            {customerDetails?.email && (
              <Typography
                variant="body1"
                display={"flex"}
                gap={"4px"}
                alignItems={"center"}
                fontSize={"13px"}
              >
                Email :{" "}
                <Typography
                  variant="body1"
                  color="#1064ED"
                  fontWeight={700}
                  letterSpacing={"0.32px"}
                  fontSize={"13px"}
                >
                  {customerDetails?.email}
                </Typography>
              </Typography>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Divider
        sx={{
          borderColor: "#464A4F26",
        }}
      />

      <Grid container mt={"18px"} mb={"22px"}>
        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          pl={matches ? "5px" : "24px"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="body1" textAlign={"right"} fontSize={"13px"}>
              Location:
            </Typography>
            <Location />
            <Typography
              variant="body1"
              display={"flex"}
              gap={"4px"}
              fontSize={"13px"}
              alignItems={"center"}
            >
              {customerDetails?.location}
              <Typography
                variant="body1"
                color="#1064ED"
                fontSize={"13px"}
                onClick={(e) => setMapView(e.currentTarget)}
              >
                {" "}
                &nbsp;View
              </Typography>
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"center"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="body1" textAlign={"right"} fontSize={"13px"}>
              Account Status:
            </Typography>
            <Chip
              sx={{ color: "#fff" }}
              label={customerDetails?.isActive ? "Active" : "Inactive"}
              className="consumer_left_card_status_chip"
            />
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"flex-end"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Clock />
            <Typography variant="body1" textAlign={"right"} fontSize={"13px"}>
              Billing Type:
            </Typography>
            <Typography variant="body1" fontSize={"13px"}>
              {customerDetails?.additionalInfo?.consumerBillingType}
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <Grid container mt={"18px"} mb={"22px"}>
        <Grid size={{ xs: 4, md: 4, lg: 4 }} container>
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="body1" textAlign={"right"} fontSize={"13px"}>
              Billing Cycle:
            </Typography>
            <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
              <Calendar />
            </IconButton>
            <Typography variant="body1" fontSize={"13px"}>
              {/* {customerDetails?.billingCycle} */}
              1st of every month
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"center"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="body1" textAlign={"right"} fontSize={"13px"}>
              Last Payment Date:
            </Typography>
            <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
              <Calendar />
            </IconButton>
            <Typography variant="body1" fontSize={"13px"}>
              {/* {customerDetails?.lastPaymentDate} */}
              {/* {formatDate(new Date(), "DD MMM YY")} */}-
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"flex-end"}
        >
          <Stack direction={"row"} alignItems={"center"} gap={"8px"}>
            <Typography variant="body1" textAlign={"right"} fontSize={"13px"}>
              Balance:
            </Typography>
            <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
              {/* <Typography variant="body1" fontSize={"13px"}>
                &#8377; 250
              </Typography> */}
              <Typography
                variant="body1"
                color="#1064ED"
                fontSize={"13px"}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => setOpenBalanceModal(true)}
              >
                Check Balance
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      {/* <Grid container>
        <Grid size={{ xs: 4, md: 3, lg: 4 }} container>
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Billing Type:
            </Typography>
            <Typography variant="caption">
              {customerDetails?.additionalInfo?.consumerBillingType}
            </Typography>
          </Stack>
        </Grid>
      </Grid> */}

      <DateAndTimePopover
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        startDate={startDate}
        endDate={endDate}
        timeRange={timeRange}
        setTimeRange={(timeRange) => setTimeRange(timeRange as number[])}
        handleDateChange={handleDateChange}
        handleDateSubmit={() => {}}
      />

      <MapViewPopover mapView={mapView} setMapView={setMapView} />

      <BalanceDetailModal
        open={openBalanceModal}
        onClose={() => setOpenBalanceModal(false)}
        customerId={customerId}
      />
    </Card>
  );
};

export default ConsumerDetailLeftCard;
